import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './PendingApproval.scss';

interface PendingJob {
  id: number;
  title: string;
  company_name: string;
  location: string;
  status: string;
  created_at: string;
  employer_email: string;
  employer_first_name: string;
  employer_last_name: string;
}

interface PendingCV {
  id: number;
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  location: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
}

interface PendingTemplate {
  id: number;
  category: string;
  value: string;
  submitted_by: number;
  submitter_email: string;
  created_at: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  institution: 'Mokymo įstaigos',
  specialization: 'Specialybės',
  degree: 'Laipsniai',
  work_field: 'Darbo sritys',
  position: 'Pareigos',
  work_location: 'Darbo vietos',
  language: 'Kalbos',
};

const PendingApproval: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'jobs' | 'cvs' | 'templates'>('jobs');
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [pendingCVs, setPendingCVs] = useState<PendingCV[]>([]);
  const [pendingTemplates, setPendingTemplates] = useState<PendingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [declineId, setDeclineId] = useState<{ id: number; type: 'job' | 'cv' } | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [editingJob, setEditingJob] = useState<any | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, cvsRes, templatesRes] = await Promise.all([
        api.get('/admin/pending-jobs'),
        api.get('/admin/pending-cvs'),
        api.get('/cv-templates/pending').catch(() => ({ data: [] }))
      ]);
      setPendingJobs(jobsRes.data.data || []);
      setPendingCVs(cvsRes.data.data || []);
      setPendingTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
    } catch (err) {
      console.error('Failed to load pending items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (id: number, type: 'job' | 'cv') => {
    setActionLoading(id);
    try {
      const endpoint = type === 'job' ? `/admin/jobs/${id}/approve` : `/admin/cvs/${id}/approve`;
      await api.patch(endpoint);
      if (type === 'job') {
        setPendingJobs(prev => prev.filter(j => j.id !== id));
      } else {
        setPendingCVs(prev => prev.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!declineId) return;
    setActionLoading(declineId.id);
    try {
      const endpoint = declineId.type === 'job'
        ? `/admin/jobs/${declineId.id}/decline`
        : `/admin/cvs/${declineId.id}/decline`;
      await api.patch(endpoint, { reason: declineReason });
      if (declineId.type === 'job') {
        setPendingJobs(prev => prev.filter(j => j.id !== declineId.id));
      } else {
        setPendingCVs(prev => prev.filter(c => c.id !== declineId.id));
      }
    } catch (err) {
      console.error('Decline failed:', err);
    } finally {
      setActionLoading(null);
      setDeclineId(null);
      setDeclineReason('');
    }
  };

  const handleApproveTemplate = async (id: number) => {
    setActionLoading(id);
    try {
      await api.post(`/cv-templates/pending/${id}/approve`);
      setPendingTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Template approve failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectTemplate = async (id: number) => {
    setActionLoading(id);
    try {
      await api.post(`/cv-templates/pending/${id}/reject`);
      setPendingTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Template reject failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenEdit = async (jobId: number) => {
    setEditLoading(true);
    try {
      const res = await api.get(`/admin/jobs/${jobId}`);
      setEditingJob(res.data.data);
    } catch (err) {
      console.error('Failed to load job:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;
    setEditSaving(true);
    try {
      await api.put(`/admin/jobs/${editingJob.id}`, editingJob);
      // Update card in list
      setPendingJobs(prev => prev.map(j => j.id === editingJob.id
        ? { ...j, title: editingJob.title, location: editingJob.location, company_name: editingJob.company_name }
        : j
      ));
      setEditingJob(null);
    } catch (err) {
      console.error('Failed to save job:', err);
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const jobCount = pendingJobs.length;
  const cvCount = pendingCVs.length;
  const templateCount = pendingTemplates.length;

  return (
    <div className="pending-approval-page">
      <div className="page-header">
        <h1>Laukia patvirtinimo</h1>
        <span className="total-badge">{jobCount + cvCount + templateCount}</span>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Darbo skelbimai
          {jobCount > 0 && <span className="tab-badge">{jobCount}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'cvs' ? 'active' : ''}`}
          onClick={() => setActiveTab('cvs')}
        >
          CV
          {cvCount > 0 && <span className="tab-badge">{cvCount}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Šablonai
          {templateCount > 0 && <span className="tab-badge">{templateCount}</span>}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Kraunama...</div>
      ) : activeTab === 'jobs' ? (
        jobCount === 0 ? (
          <div className="empty-state">Nėra laukiančių darbo skelbimų</div>
        ) : (
          <div className="pending-list">
            {pendingJobs.map(job => (
              <div key={job.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{job.title}</h3>
                  <div className="card-meta">
                    <span>{job.company_name}</span>
                    <span className="separator">·</span>
                    <span>{job.location}</span>
                  </div>
                  <div className="card-submitter">
                    Pateikė: {job.employer_first_name} {job.employer_last_name} ({job.employer_email})
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(job.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-edit"
                    onClick={() => handleOpenEdit(job.id)}
                    disabled={editLoading}
                  >
                    Redaguoti
                  </button>
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(job.id, 'job')}
                    disabled={actionLoading === job.id}
                  >
                    {actionLoading === job.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => setDeclineId({ id: job.id, type: 'job' })}
                    disabled={actionLoading === job.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : activeTab === 'cvs' ? (
        cvCount === 0 ? (
          <div className="empty-state">Nėra laukiančių CV</div>
        ) : (
          <div className="pending-list">
            {pendingCVs.map(cv => (
              <div key={cv.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{cv.first_name} {cv.last_name}</h3>
                  <div className="card-meta">
                    <span>{cv.title || 'CV'}</span>
                    {cv.location && (
                      <>
                        <span className="separator">·</span>
                        <span>{cv.location}</span>
                      </>
                    )}
                  </div>
                  <div className="card-submitter">
                    Pateikė: {cv.user_first_name} {cv.user_last_name} ({cv.user_email})
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(cv.updated_at || cv.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(cv.id, 'cv')}
                    disabled={actionLoading === cv.id}
                  >
                    {actionLoading === cv.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => setDeclineId({ id: cv.id, type: 'cv' })}
                    disabled={actionLoading === cv.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        templateCount === 0 ? (
          <div className="empty-state">Nėra laukiančių šablonų patvirtinimui</div>
        ) : (
          <div className="pending-list">
            {pendingTemplates.map(tpl => (
              <div key={tpl.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{tpl.value}</h3>
                  <div className="card-meta">
                    <span className="template-category-badge">{CATEGORY_LABELS[tpl.category] || tpl.category}</span>
                  </div>
                  <div className="card-submitter">
                    Pateikė: {tpl.submitter_email}
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(tpl.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApproveTemplate(tpl.id)}
                    disabled={actionLoading === tpl.id}
                  >
                    {actionLoading === tpl.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => handleRejectTemplate(tpl.id)}
                    disabled={actionLoading === tpl.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="decline-overlay" onClick={() => setEditingJob(null)}>
          <div className="edit-job-modal edit-job-modal--wide" onClick={e => e.stopPropagation()}>
            <h3>Redaguoti skelbimą #{editingJob.id}</h3>

            {/* ── Pagrindinė informacija ── */}
            <div className="edit-section-title">Pagrindinė informacija</div>
            <div className="edit-grid-2">
              <div className="edit-field">
                <label>Pavadinimas</label>
                <input value={editingJob.title || ''} onChange={e => setEditingJob((p: any) => ({ ...p, title: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Darbo kalba</label>
                <input value={editingJob.job_language || ''} onChange={e => setEditingJob((p: any) => ({ ...p, job_language: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Vieta</label>
                <input value={editingJob.location || ''} onChange={e => setEditingJob((p: any) => ({ ...p, location: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Darbo sritis</label>
                <input value={editingJob.work_field || ''} onChange={e => setEditingJob((p: any) => ({ ...p, work_field: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Pareigybės tipas</label>
                <input value={editingJob.position_type || ''} onChange={e => setEditingJob((p: any) => ({ ...p, position_type: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Darbo tipas</label>
                <input value={editingJob.job_type || ''} onChange={e => setEditingJob((p: any) => ({ ...p, job_type: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Darbo laikas</label>
                <input value={editingJob.work_time || ''} onChange={e => setEditingJob((p: any) => ({ ...p, work_time: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Darbo pradžia</label>
                <input value={editingJob.start_date_type || ''} onChange={e => setEditingJob((p: any) => ({ ...p, start_date_type: e.target.value }))} />
              </div>
            </div>

            {/* ── Atlyginimas ── */}
            <div className="edit-section-title">Atlyginimas</div>
            <div className="edit-grid-3">
              <div className="edit-field">
                <label>Nuo (€)</label>
                <input type="number" value={editingJob.salary_min || ''} onChange={e => setEditingJob((p: any) => ({ ...p, salary_min: e.target.value ? parseFloat(e.target.value) : null }))} />
              </div>
              <div className="edit-field">
                <label>Iki (€)</label>
                <input type="number" value={editingJob.salary_max || ''} onChange={e => setEditingJob((p: any) => ({ ...p, salary_max: e.target.value ? parseFloat(e.target.value) : null }))} />
              </div>
              <div className="edit-field edit-field--checkbox">
                <label className="checkbox-inline">
                  <input type="checkbox" checked={!!editingJob.hide_salary} onChange={e => setEditingJob((p: any) => ({ ...p, hide_salary: e.target.checked }))} />
                  Nerodyti atlyginimo
                </label>
              </div>
            </div>

            {/* ── Turinys ── */}
            <div className="edit-section-title">Turinys</div>
            <div className="edit-field">
              <label>Aprašymas</label>
              <textarea rows={5} value={editingJob.description || ''} onChange={e => setEditingJob((p: any) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label>Reikalavimai kandidatams</label>
              <textarea rows={4} value={editingJob.requirements || ''} onChange={e => setEditingJob((p: any) => ({ ...p, requirements: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label>Privalumai</label>
              <textarea rows={3} value={editingJob.benefits || ''} onChange={e => setEditingJob((p: any) => ({ ...p, benefits: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label>Įmonė siūlo</label>
              <textarea rows={3} value={editingJob.company_offers || ''} onChange={e => setEditingJob((p: any) => ({ ...p, company_offers: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label>Papildoma informacija</label>
              <textarea rows={3} value={editingJob.additional_info || ''} onChange={e => setEditingJob((p: any) => ({ ...p, additional_info: e.target.value }))} />
            </div>

            {/* ── Įmonės informacija ── */}
            <div className="edit-section-title">Įmonės informacija</div>
            <div className="edit-grid-2">
              <div className="edit-field">
                <label>Įmonė</label>
                <input value={editingJob.company_name || ''} onChange={e => setEditingJob((p: any) => ({ ...p, company_name: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Veiklos sritis</label>
                <input value={editingJob.company_activity || ''} onChange={e => setEditingJob((p: any) => ({ ...p, company_activity: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Kontaktinis el. paštas</label>
                <input type="email" value={editingJob.contact_email || ''} onChange={e => setEditingJob((p: any) => ({ ...p, contact_email: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Svetainė</label>
                <input value={editingJob.website_url || ''} onChange={e => setEditingJob((p: any) => ({ ...p, website_url: e.target.value }))} />
              </div>
            </div>
            <div className="edit-field">
              <label>Įmonės aprašymas</label>
              <textarea rows={3} value={editingJob.company_description || ''} onChange={e => setEditingJob((p: any) => ({ ...p, company_description: e.target.value }))} />
            </div>

            {/* ── Kita ── */}
            <div className="edit-section-title">Kita</div>
            <div className="edit-grid-2">
              <div className="edit-field">
                <label>Prašymus siųsti iki</label>
                <input type="date" value={editingJob.application_deadline ? editingJob.application_deadline.split('T')[0] : ''} onChange={e => setEditingJob((p: any) => ({ ...p, application_deadline: e.target.value }))} />
              </div>
              <div className="edit-field edit-field--checkboxes">
                <label>Nustatymai</label>
                <label className="checkbox-inline">
                  <input type="checkbox" checked={!!editingJob.guarantee_confidentiality} onChange={e => setEditingJob((p: any) => ({ ...p, guarantee_confidentiality: e.target.checked }))} />
                  Konfidencialumą garantuojame
                </label>
                <label className="checkbox-inline">
                  <input type="checkbox" checked={!!editingJob.inform_selected_only} onChange={e => setEditingJob((p: any) => ({ ...p, inform_selected_only: e.target.checked }))} />
                  Informuosime tik atrinktus
                </label>
                <label className="checkbox-inline">
                  <input type="checkbox" checked={!!editingJob.hide_company} onChange={e => setEditingJob((p: any) => ({ ...p, hide_company: e.target.checked }))} />
                  Nerodyti įmonės duomenų
                </label>
              </div>
            </div>

            <div className="decline-actions">
              <button className="btn-cancel" onClick={() => setEditingJob(null)}>Atšaukti</button>
              <button className="btn-approve" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline modal */}
      {declineId && (
        <div className="decline-overlay" onClick={() => setDeclineId(null)}>
          <div className="decline-modal" onClick={e => e.stopPropagation()}>
            <h3>Atmesti {declineId.type === 'job' ? 'skelbimą' : 'CV'}?</h3>
            <label>
              Atmetimo priežastis (bus parodyta vartotojui):
              <textarea
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                rows={3}
                placeholder="Įveskite priežastį, kurią matys vartotojas..."
              />
            </label>
            <div className="decline-actions">
              <button className="btn-cancel" onClick={() => setDeclineId(null)}>Atšaukti</button>
              <button className="btn-decline-confirm" onClick={handleDeclineConfirm}>
                Atmesti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApproval;
